import React, { useCallback, useMemo } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';
import { AppTakeoverMenuSectionTransition } from 'features/app-takeover/components/app-takeover-menu-section-transition';
import { useAppTakeoverMenuSection } from 'features/app-takeover/hooks/use-app-takeover';
import { MenuHeaderNavigation } from 'features/menu/components/menu-header-navigation';
import { MenuItemUnavailable } from 'features/menu/components/menu-item-unavailable';
import { MenuNavigationTabs } from 'features/menu/components/menu-navigation-tabs/menu-navigation-tabs';
import { MenuSection } from 'features/menu/components/menu-section/menu-section';
import { MenuSkeletonLoader } from 'features/menu/components/menu-tile-grid/menu-tile-grid-loading';
import { useTargetItemIds } from 'features/menu/hooks/use-target-item-ids';
import { pinTargetItems } from 'features/menu/utils/pin-target-items';
import { useStoreContext } from 'features/store';
import { StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { FullWidthAutoHeightImage } from 'components/picture';
import { MenuDayPartUnavailable } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-day-part-unavailable';
import { UnavailableSectionRecovery } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/unavailable-section-recovery';
import { asPreviewMenuEntry } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/as-preview-menu-entry';
import { useClearUnavailableItemIdWhenAvailable } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-clear-unavailable-item-id-when-available';
import { useOneMenuCatalog } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-one-menu-catalog';
import { useUnavailableItemId } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-unavailable-item-id';
import { findMainMenuNodeById } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/find-main-menu-node-by-id';
import { injectAndPinUnavailableSectionItems } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/inject-and-pin-unavailable-section-items';
import { isEntryOutOfActiveDayParts } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/is-entry-out-of-active-day-parts';
import { resolveDeeplinkUnavailableItemId } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/resolve-deeplink-unavailable-item-id';
import { useDeeplinksFlowExperiment } from 'experiments/deeplinks-flow-feature/use-deeplinks-flow-experiment';
import { useLocale } from 'state/intl';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { isProductRouteMenuEntry } from 'state/unified-menu/utils';
import { routes } from 'utils/routing';

// NEW one menu
export const MenuSectionPage = () => {
  const router = useRouter();
  const { language } = useLocale();
  const { store } = useStoreContext();
  const {
    isEnabled: isDeeplinksFlowEnabled,
    isLoading: isExperimentLoading,
  } = useDeeplinksFlowExperiment();
  const {
    mainMenu,
    indexedMenu,
    isLoading,
    loadedStoreNumber,
    activeDayParts,
    isDayPartsSourceResolved,
  } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();
  const dayPartIds = isDayPartsSourceResolved
    ? activeDayParts.map(dayPart => dayPart.key)
    : undefined;
  const { id } = useLocalSearchParams<{ id: string }>();
  const targetItemIds = useTargetItemIds();
  const unavailableItemIdParam = useUnavailableItemId();
  const treatmentUnavailableItemId = isDeeplinksFlowEnabled ? unavailableItemIdParam : undefined;

  const storeNumber = store?.number ?? null;
  const isStoreMenuReady =
    !isLoading && !!storeNumber && loadedStoreNumber === storeNumber && indexedMenu.isLoaded();

  const section = useMemo(() => (id ? findMainMenuNodeById(mainMenu ?? [], id) : undefined), [
    id,
    mainMenu,
  ]);

  // Catalog for unavailable-item inject, and for daypart copy when the section
  // is missing/empty on the store menu.
  const needsCatalog =
    Boolean(treatmentUnavailableItemId) || (isDeeplinksFlowEnabled && isStoreMenuReady && !section);
  const { findEntry: findCatalogEntry, loading: isCatalogLoading } = useOneMenuCatalog({
    skip: !needsCatalog,
  });

  const isAvailableAtStore = useCallback(
    (entryId: string) => {
      const storeEntry = indexedMenu.find(entryId);
      return Boolean(storeEntry && isProductRouteMenuEntry(storeEntry, posServiceMode, dayPartIds));
    },
    [dayPartIds, indexedMenu, posServiceMode]
  );

  const unavailableItemNowAvailable = Boolean(
    treatmentUnavailableItemId && isStoreMenuReady && isAvailableAtStore(treatmentUnavailableItemId)
  );

  useClearUnavailableItemIdWhenAvailable({
    unavailableItemId: treatmentUnavailableItemId,
    isStoreMenuReady,
    isAvailableAtStore: unavailableItemNowAvailable,
  });

  const { title, options, deeplinkUnavailableItemId } = useMemo(() => {
    const sectionChildren = section?.children ?? [];

    // Control / treatment without unavailableItemId: pin marketing targets only —
    // never inject catalog nodes or enable unavailable chrome.
    if (!treatmentUnavailableItemId) {
      return {
        title: section?.name ?? '',
        options: pinTargetItems(sectionChildren, targetItemIds),
        deeplinkUnavailableItemId: undefined,
      };
    }

    // Section missing at this store — do not inject orphan unavailable items
    // (e.g. picker whose parent section is not on the store menu). Full Menu
    // recovery handles that path below.
    if (!section) {
      return {
        title: '',
        options: [],
        deeplinkUnavailableItemId: undefined,
      };
    }

    const resolveEntry = (entryId: string): ILocalizedMenuEntry | undefined => {
      const storeEntry = indexedMenu.find(entryId);
      if (storeEntry) {
        return storeEntry;
      }
      const catalogEntry = findCatalogEntry(entryId);
      return catalogEntry ? asPreviewMenuEntry(catalogEntry, language) : undefined;
    };

    const injectTargets = Array.from(new Set([...targetItemIds, treatmentUnavailableItemId]));

    const { items } = injectAndPinUnavailableSectionItems(
      sectionChildren,
      injectTargets,
      resolveEntry,
      posServiceMode
    );

    return {
      title: section.name ?? '',
      options: items,
      deeplinkUnavailableItemId: resolveDeeplinkUnavailableItemId({
        unavailableItemIdParam: treatmentUnavailableItemId,
        isAvailableAtStore,
      }),
    };
  }, [
    findCatalogEntry,
    indexedMenu,
    isAvailableAtStore,
    language,
    posServiceMode,
    section,
    targetItemIds,
    treatmentUnavailableItemId,
  ]);

  const handleUnavailableChooseRestaurant = useCallback(() => {
    if (!deeplinkUnavailableItemId) {
      return;
    }
    const entry =
      indexedMenu.find(deeplinkUnavailableItemId) ??
      (findCatalogEntry(deeplinkUnavailableItemId)
        ? asPreviewMenuEntry(findCatalogEntry(deeplinkUnavailableItemId)!, language)
        : undefined);
    const typeSegment = String(entry?.type ?? 'item').toLowerCase();
    // After store pick, useSelectStore opens this PDP; available → product modal,
    // unavailable → MenuProductPage redirects to section chrome.
    router.navigate({
      pathname: routes.minimalStoreLocator,
      params: {
        redirectUrl: `${routes.menu}/${typeSegment}/${deeplinkUnavailableItemId}`,
        unavailableItemId: deeplinkUnavailableItemId,
      },
    });
  }, [deeplinkUnavailableItemId, findCatalogEntry, indexedMenu, language, router]);

  const {
    headerBackgroundColor,
    headerBackgroundImage,
    headerBackgroundImageLayout,
    itemBackgroundColor: takeoverBg,
    footerImage: takeoverFooterImage,
    footerImageOffset: takeoverFooterImageOffset,
  } = useAppTakeoverMenuSection(id);

  const headerBackgroundColorOverride = headerBackgroundImage
    ? 'transparent'
    : headerBackgroundColor
    ? headerBackgroundColor
    : undefined;
  const shouldCoverTabs = headerBackgroundImageLayout === 'cover-tabs';

  const storeSectionChildrenCount = section?.children?.length ?? 0;
  const isSectionUnavailable = isStoreMenuReady && (!section || storeSectionChildrenCount === 0);

  const sectionEntry = useMemo(() => {
    if (!id) {
      return undefined;
    }
    const storeEntry = indexedMenu.find(id);
    if (storeEntry) {
      return storeEntry;
    }
    const catalogEntry = findCatalogEntry(id);
    return catalogEntry ? asPreviewMenuEntry(catalogEntry, language) : undefined;
  }, [findCatalogEntry, id, indexedMenu, language]);

  const sectionDayParts = sectionEntry?.dayParts ?? [];
  const isSectionOutOfDayPart = isEntryOutOfActiveDayParts(sectionEntry, dayPartIds);

  if (isSectionUnavailable) {
    // Wait for Amplitude before control vs treatment recovery — otherwise a
    // treatment user can act on the control unavailable modal first.
    if (isExperimentLoading) {
      return (
        <SafeAreaView edges={['bottom']} style={{ flex: 1 }}>
          <MenuSkeletonLoader />
        </SafeAreaView>
      );
    }

    // Missing section may be a daypart mismatch resolved only by catalog metadata.
    // Wait for catalog settle before opening Full Menu recovery.
    if (isDeeplinksFlowEnabled && !section && isCatalogLoading) {
      return (
        <SafeAreaView edges={['bottom']} style={{ flex: 1 }}>
          <MenuSkeletonLoader />
        </SafeAreaView>
      );
    }

    // Daypart-only: show “Category only available on …” — do not open Full Menu
    // section recovery chrome.
    if (isDeeplinksFlowEnabled && isSectionOutOfDayPart && sectionDayParts.length > 0) {
      return (
        <SafeAreaView edges={['bottom']} style={{ flex: 1 }}>
          <MenuDayPartUnavailable kind="category" dayParts={sectionDayParts} />
        </SafeAreaView>
      );
    }

    if (isDeeplinksFlowEnabled && id) {
      return (
        <UnavailableSectionRecovery
          sectionId={id}
          targetItemIds={targetItemIds}
          unavailableItemId={treatmentUnavailableItemId}
        />
      );
    }

    return (
      <SafeAreaView edges={['bottom']} style={{ flex: 1 }}>
        <MenuHeaderNavigation title={title} key={title || id} />
        <MenuItemUnavailable isOffer={false} />
      </SafeAreaView>
    );
  }

  const tabs = (
    <>
      {/* key is used to force re-render and display Section name after reloading page, 
          there is a memo messing up prop comparison */}
      <MenuHeaderNavigation
        title={title}
        key={title}
        headerBackgroundColorOverride={headerBackgroundColorOverride}
      />
      <MenuNavigationTabs
        mainMenu={mainMenu}
        headerBackgroundColorOverride={headerBackgroundColorOverride}
      />
    </>
  );

  const takeoverFooterSpacer = takeoverFooterImageOffset && (
    <View style={{ width: '100%', height: takeoverFooterImageOffset }}></View>
  );

  return (
    <SafeAreaView edges={['bottom']} style={{ flex: 1, backgroundColor: takeoverBg }}>
      <AppTakeoverMenuSectionTransition sectionId={id} />
      {headerBackgroundImage ? (
        <View>
          <FullWidthAutoHeightImage
            source={headerBackgroundImage}
            contentFit={shouldCoverTabs ? 'cover' : undefined}
            style={shouldCoverTabs ? styles.coverTabsBackground : undefined}
          />
          <View style={shouldCoverTabs ? undefined : styles.tabsOverlay}>{tabs}</View>
        </View>
      ) : (
        tabs
      )}
      <MenuSection
        data={options}
        footerSpacer={takeoverFooterSpacer}
        deeplinkUnavailableItemId={deeplinkUnavailableItemId}
        onUnavailableChooseRestaurant={
          deeplinkUnavailableItemId ? handleUnavailableChooseRestaurant : undefined
        }
      />
      {takeoverFooterImage && (
        <View pointerEvents="none" style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
          <FullWidthAutoHeightImage source={takeoverFooterImage} />
        </View>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  coverTabsBackground: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
    aspectRatio: undefined,
  },
  tabsOverlay: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    left: 0,
  },
});
